import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const RefundsCancellationsDisputes = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Refunds, Cancellations & Disputes Policy</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BackButton
                onClick={() => navigate('/legal')}
                variant="link"
                ariaLabel="Back to Legal Center"
              />
              <span className="text-blue-600 hover:text-blue-500 font-medium">
                Back to Legal Center
              </span>
            </div>
          </div>

          <div className="prose max-w-none">
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-4">
                Effective Date: [Insert Date]
              </p>
              <p className="text-gray-700 mb-6">
                This policy explains when buyers may receive a refund, when sellers may retain payment, and how disputes are resolved on Vesta. By buying or selling on Vesta, you agree to these rules in addition to our Terms of Service.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. General Principles</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Food is perishable — refunds are handled differently than for non-perishable goods.</li>
              <li className="mb-2">Vesta encourages buyers to contact sellers directly through the Platform to resolve issues before escalating.</li>
              <li className="mb-2">All refund and dispute timelines are measured from the scheduled delivery, pickup, or service time.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. When Buyers May Be Eligible for a Refund</h2>
            <p className="text-gray-700 mb-4">Buyers may be eligible for a full or partial refund if:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">The order never arrived or was not available for pickup.</li>
              <li className="mb-2">The wrong items were provided.</li>
              <li className="mb-2">The product was unsafe to consume at the time of handoff (e.g., spoiled, undercooked when not intended).</li>
              <li className="mb-2">The service (e.g., catering) was not performed as agreed in the confirmed order details.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              Refund eligibility requires photo evidence or other proof submitted within 24 hours of delivery, pickup, or service.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. When Refunds Are Not Available</h2>
            <p className="text-gray-700 mb-4">Refunds are generally not available if:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">The buyer changes their mind after pickup or delivery.</li>
              <li className="mb-2">The buyer fails to collect an order during the pickup window.</li>
              <li className="mb-2">The product is mishandled by the buyer after delivery/pickup (e.g., left unrefrigerated).</li>
              <li className="mb-2">The product matches the listing description and was delivered as agreed.</li>
              <li className="mb-2">The buyer missed the delivery window and redelivery was not arranged.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Seller Cancellation Rules</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">If a seller cancels before the fulfillment date, all buyer payments must be refunded in full.</li>
              <li className="mb-2">Sellers may be subject to account review if they cancel confirmed orders without valid cause.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Buyer Cancellation Rules</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Cancellation terms must be stated in the seller's listing or service agreement.</li>
              <li className="mb-2">Preorders, custom, or made-to-order items may be non-refundable once preparation has begun.</li>
              <li className="mb-2">For catering or in-home chef bookings, deposits may be retained if cancellation occurs within the seller's stated non-refundable period.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Partial Refunds</h2>
            <p className="text-gray-700 mb-4">Partial refunds may be issued when:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Only part of the order is missing or incorrect.</li>
              <li className="mb-2">Service was delivered but with material quality issues.</li>
              <li className="mb-2">Buyer and seller mutually agree to a reduced refund amount.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. How to Request a Refund</h2>
            <ol className="list-decimal pl-6 mb-6 text-gray-700">
              <li className="mb-2">Contact the seller through Vesta messages within 24 hours of delivery/pickup/service.</li>
              <li className="mb-2">Provide order details and evidence (photos, delivery confirmation, description of issue).</li>
              <li className="mb-2">If the seller does not respond or resolve the issue within 48 hours, the buyer can escalate to Vesta.</li>
            </ol>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Dispute Escalation</h2>
            <p className="text-gray-700 mb-4">When a dispute is escalated:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Vesta will review all communication and evidence.</li>
              <li className="mb-2">Decisions are final and binding.</li>
              <li className="mb-2">We may issue a refund, partial refund, or deny the request based on the facts.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Timeline Requirements</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Timeframes:</h3>
              <ul className="list-disc pl-6 text-gray-700">
                <li className="mb-2"><strong>24 hours:</strong> Buyer must report issues and request refunds</li>
                <li className="mb-2"><strong>48 hours:</strong> Seller must respond to buyer concerns</li>
                <li className="mb-2"><strong>72 hours:</strong> Vesta will review escalated disputes</li>
              </ul>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Evidence Requirements</h2>
            <p className="text-gray-700 mb-4">To support refund requests, buyers should provide:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Clear photos of the issue (e.g., spoiled food, wrong items)</li>
              <li className="mb-2">Screenshots of order confirmation and delivery details</li>
              <li className="mb-2">Communication records with the seller</li>
              <li className="mb-2">Any relevant receipts or documentation</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Special Circumstances</h2>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Force Majeure Events</h3>
            <p className="text-gray-700 mb-4">
              In cases of natural disasters, government actions, or other events beyond control, Vesta may modify refund policies to ensure fair treatment of all parties.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Food Safety Issues</h3>
            <p className="text-gray-700 mb-6">
              Any confirmed food safety violations will result in immediate refunds and may lead to seller account suspension pending investigation.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700">
                For questions about this policy or to escalate a dispute:<br />
                <strong>Email:</strong> disputes@vesta.com<br />
                <strong>Support Hours:</strong> Monday-Friday, 9 AM - 6 PM EST
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundsCancellationsDisputes; 